import { useState, useRef, useCallback, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  LogOut,
  ChevronsUpDown,
  MoreVertical,
  Eye,
  Pin,
  Building2,
  ChevronDown,
  ChevronRight,
  Rocket,
  LockKeyhole,
  ArrowLeft,
  CreditCard,
  Languages,
  Plug,
  Search,
  ShieldCheck,
  UserRound,
  Bell,
  Calendar,
  Store,
  LayoutGrid,
  Users,
  ListChecks,
  CalendarCheck,
  Repeat,
  GraduationCap,
} from "lucide-react";
import { APPOINTMENT_BOOKING_ENABLED, PROGRAMME_MANAGEMENT_ENABLED } from "@/config/featureFlags";

import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useCustomerReviewCount } from "@/hooks/useCustomerReviewCount";
import { useOutlet } from "@/contexts/OutletContext";
import { supabase } from "@/integrations/supabase/client";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarMenuSub,
  SidebarFooter,
  SidebarHeader,
  SidebarSeparator,
} from "@/components/ui/sidebar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { SIDEBAR_GROUPS, ALL_SIDEBAR_ITEMS, SECTION_PARENT_KEY, type SidebarItem } from "./sidebarConfig";
import OutletSwitcher from "@/components/admin_dashboard/OutletSwitcher";
import { usePinnedTabs } from "@/hooks/usePinnedTabs";
import { planHasFeature } from "@/lib/planEntitlements";
import { useTerminology } from "@/contexts/TerminologyContext";
import {
  filterSettingsNavItems,
  resolveSettingsPanel,
  SETTINGS_NAV_GROUPS,
  type SettingsPanel,
} from "@/components/admin_dashboard/settingsNavigation";

// Pin-to-sidebar is hidden for now (no pin affordance, no "Pinned tabs" group).
// Flip to true to restore it.
const SHOW_PINNED_TABS = false;

// Helpers

const isTouchDevice = () =>
  "ontouchstart" in window || navigator.maxTouchPoints > 0;

// SidebarTabItem

interface SidebarTabItemProps {
  item: SidebarItem;
  isActive: boolean;
  isPinned: boolean;
  onToggleActive: () => void;
  onTogglePin: () => void;
  onPreview?: () => void;
  hidePinAction?: boolean;
  /** Optional data-tour selector value, used by the first-time onboarding tour. */
  dataTour?: string;
  /** Optional notification count shown as a badge beside the label. */
  badgeCount?: number;
  /** Tooltip for the badge. */
  badgeTitle?: string;
  /** Shows that the destination opens with an in-feature upgrade overlay. */
  isLocked?: boolean;
}

const SidebarTabItem = ({
  item,
  isActive,
  isPinned,
  onToggleActive,
  onTogglePin,
  onPreview,
  hidePinAction,
  dataTour,
  badgeCount,
  badgeTitle,
  isLocked = false,
}: SidebarTabItemProps) => {
  const { customers } = useTerminology();
  const [showPinTouch, setShowPinTouch] = useState(false);
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const autoDismissTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const touchMoved = useRef(false);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (longPressTimer.current) clearTimeout(longPressTimer.current);
      if (autoDismissTimer.current) clearTimeout(autoDismissTimer.current);
    };
  }, []);

  const handleTouchStart = useCallback(() => {
    if (!isTouchDevice()) return;
    touchMoved.current = false;
    longPressTimer.current = setTimeout(() => {
      setShowPinTouch(true);
      // Auto-dismiss after 3 seconds
      autoDismissTimer.current = setTimeout(() => {
        setShowPinTouch(false);
      }, 3000);
    }, 500);
  }, []);

  const handleTouchMove = useCallback(() => {
    touchMoved.current = true;
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  }, []);

  const handlePinClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      onTogglePin();
      // Dismiss the touch-revealed pin immediately
      setShowPinTouch(false);
      if (autoDismissTimer.current) {
        clearTimeout(autoDismissTimer.current);
        autoDismissTimer.current = null;
      }
    },
    [onTogglePin],
  );

  const handlePreviewClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      onPreview?.();
    },
    [onPreview],
  );

  // Pin visibility:
  //  - Desktop: always visible when pinned, shown on hover otherwise (via group-hover)
  //  - Touch:   always visible when pinned, shown after long-press otherwise
  const showPin = isPinned || showPinTouch;

  const Icon = item.icon;

  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        isActive={isActive}
        tooltip={item.label}
        onClick={onToggleActive}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        className="group/tab"
        data-tour={dataTour}
      >
        <Icon className="h-4 w-4 shrink-0" />
        <span className="flex-1 truncate">{item.key === "customers" ? customers : item.label}</span>

        {/* Action icons container — reserves space so layout doesn't shift */}
        <span className="ml-auto flex items-center gap-1 shrink-0 group-data-[collapsible=icon]:hidden">
          {/* Notification badge (e.g. pending actions awaiting approval) */}
          {!!badgeCount && badgeCount > 0 && (
            <span
              title={badgeTitle ?? `${badgeCount} awaiting review`}
              className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-500 px-1.5 text-[10px] font-semibold text-white"
            >
              {badgeCount}
            </span>
          )}

          {isLocked && (
            <span
              title={`${item.label} requires a higher plan`}
              aria-label={`${item.label} requires a higher plan`}
              className="inline-flex h-5 w-5 items-center justify-center rounded-sm text-muted-foreground"
            >
              <LockKeyhole className="h-3.5 w-3.5" />
            </span>
          )}

          {/* Pin icon */}
          {!hidePinAction && (
            <span
              role="button"
              tabIndex={-1}
              onClick={handlePinClick}
              title={isPinned ? "Unpin tab" : "Pin tab"}
              className={`
                inline-flex items-center justify-center h-5 w-5 rounded-sm transition-opacity cursor-pointer
                hover:bg-muted-foreground/10
                ${
                  isPinned
                    ? "opacity-100"
                    : showPin
                      ? "opacity-100"
                      : "opacity-0 group-hover/tab:opacity-100"
                }
              `}
            >
              <Pin
                className={`h-3.5 w-3.5 ${
                  isPinned ? "fill-current text-primary" : "text-muted-foreground"
                }`}
              />
            </span>
          )}

          {/* Eye / preview icon — only for items with hasPreviewIcon */}
          {item.hasPreviewIcon && (
            <span
              role="button"
              tabIndex={-1}
              onClick={handlePreviewClick}
              title="View storefront"
              className="inline-flex items-center justify-center h-5 w-5 rounded-sm cursor-pointer hover:bg-muted-foreground/10"
            >
              <Eye className="h-3.5 w-3.5 text-muted-foreground" />
            </span>
          )}
        </span>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
};

// AdminSidebar

interface AdminSidebarProps {
  activeSection: string;
  onSectionChange: (section: string, extraParams?: Record<string, string>) => void;
  onExitSettings: () => void;
}

// Dibs Bookings workspace destinations, shown as their own sidebar group.
const DIBS_BOOKINGS_ITEMS: SidebarItem[] = [
  { key: "dibs-bookings", label: "Bookings", section: "bookings", icon: Calendar },
  // Enrolments / Attendance / Make-ups are the recurring programme-management suite.
  ...(PROGRAMME_MANAGEMENT_ENABLED
    ? [
        { key: "dibs-enrolments", label: "Enrolments", section: "enrolments", icon: GraduationCap },
        { key: "dibs-attendance", label: "Attendance", section: "attendance", icon: CalendarCheck },
        { key: "dibs-make-ups", label: "Make-ups", section: "make-ups", icon: Repeat },
      ]
    : []),
  { key: "dibs-waitlist", label: "Waitlist", section: "waitlist", icon: ListChecks },
  { key: "dibs-storefront", label: "Storefront", section: "business-details", icon: Store },
  { key: "dibs-listings", label: "Listing", section: "listings", icon: LayoutGrid },
  // Staff (resources) is appointment-mode only.
  ...(APPOINTMENT_BOOKING_ENABLED
    ? [{ key: "dibs-resources", label: "Staff", section: "resources", icon: Users }]
    : []),
];

const SETTINGS_ITEM_ICONS: Record<SettingsPanel, typeof UserRound> = {
  account: UserRound,
  terminology: Languages,
  branches: Building2,
  connectors: Plug,
  notifications: Bell,
  billing: CreditCard,
  "support-access": ShieldCheck,
};

const AdminSidebar = ({ activeSection, onSectionChange, onExitSettings }: AdminSidebarProps) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, signOut, businessId, isStaff, staffBusinesses, switchStaffBusiness, hasBookingsAddon, subscriptionTier } = useAuth();
  const { activeOutlet } = useOutlet();
  const { pinnedKeys, togglePin } = usePinnedTabs();
  const isSettingsView = activeSection === "settings";
  const activeSettingsPanel = resolveSettingsPanel(searchParams.get("settings"));
  const [settingsSearch, setSettingsSearch] = useState("");
  const visibleSettingsItems = isStaff ? [] : filterSettingsNavItems(settingsSearch);

  // Track which collapsible groups and nested items are closed.
  const [collapsedKeys, setCollapsedKeys] = useState<Set<string>>(new Set());
  const toggleCollapsed = useCallback((key: string) => {
    setCollapsedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const { data: businessData } = useQuery({
    // Keep this separate from OutletSwitcher's smaller business query. Sharing
    // a cache key between different select lists can leave the trial fields
    // undefined until a hard refresh.
    queryKey: ["businessSidebarTrial", businessId],
    queryFn: async () => {
      if (!businessId) return null;
      const { data } = await supabase
        .from("businesses")
        .select("slug, subscription_status, next_billing_date")
        .eq("business_id", businessId)
        .maybeSingle();
      return data;
    },
    enabled: !!businessId,
    staleTime: 30000,
  });

  const slug = businessData?.slug || null;
  const trialDaysRemaining =
    businessData?.subscription_status === "trialing" && businessData.next_billing_date
      ? Math.max(
          0,
          Math.ceil(
            (new Date(businessData.next_billing_date).getTime() - Date.now()) /
              (1000 * 60 * 60 * 24),
          ),
        )
      : null;

  const handleSignOut = async () => {
    await signOut();
    navigate("/");
  };

  const openStorefrontPreview = useCallback(() => {
    const protocol = window.location.protocol;
    const host = window.location.hostname.replace(/^admin\./, "");
    const port = window.location.port ? `:${window.location.port}` : "";
    const url = (activeOutlet as any)?.slug
      ? `${protocol}//${host}${port}/${(activeOutlet as any).slug}`
      : slug
        ? `${protocol}//${host}${port}/${slug}`
        : `${protocol}//${host}${port}/book/${businessId}`;
    window.open(url, "_blank");
  }, [activeOutlet, slug, businessId]);

  const userInitial = user?.email?.charAt(0).toUpperCase() || "U";

  // Staff work in the whole product except the owner's controls. Connector
  // credentials now live in Settings; Dibs Bookings remains ordinary
  // day-to-day work and is exposed in the main navigation when active.
  // Billing remains listed here for legacy deep links; the Settings sidebar
  // exposes only Knowledge to staff.
  const STAFF_BLOCKED_KEYS = ["connectors", "billing", "collaboration"];

  // Plan-gated destinations stay visible. Selecting one opens the actual
  // feature with its tinted upgrade overlay; only staff-role restrictions hide
  // navigation items entirely.
  const { approvals: customerApprovals, suggestions: customerSuggestions } = useCustomerReviewCount(businessId);
  const badgeProps = (item: SidebarItem) => {
    if (item.key !== "customers") return {};
    const count = customerApprovals + customerSuggestions;
    if (count === 0) return {};
    const parts = [
      customerApprovals > 0 ? `${customerApprovals} action${customerApprovals === 1 ? "" : "s"} awaiting approval` : null,
      customerSuggestions > 0 ? `${customerSuggestions} relationship suggestion${customerSuggestions === 1 ? "" : "s"} to review` : null,
    ].filter(Boolean);
    return { badgeCount: count, badgeTitle: parts.join(", ") };
  };

  const itemAllowed = (item: SidebarItem) =>
    !isStaff || !STAFF_BLOCKED_KEYS.includes(item.key);
  const itemLocked = (item: SidebarItem) =>
    !!item.requiresFeature && !planHasFeature(subscriptionTier, item.requiresFeature);

  // Filter sidebar groups for staff access only.
  // Staff get a flat list (nesting stripped) of only the sections they're
  // allowed to see; owners keep the nested grouping.
  const filteredGroups = (
    isStaff
      ? SIDEBAR_GROUPS.map(group => ({
          ...group,
          items: group.items
            .flatMap(item => (item.children ? [item, ...item.children] : [item]))
            .map(item => ({ ...item, children: undefined })),
        }))
      : SIDEBAR_GROUPS.map(group => ({
          ...group,
          items: group.items
            .filter(itemAllowed)
            .map(item =>
              item.children
                ? { ...item, children: item.children.filter(itemAllowed) }
                : item,
            ),
        }))
  )
    .map(group => ({
      ...group,
      items: group.items.filter(itemAllowed),
    }))
    .filter(group => group.items.length > 0);

  // Show the Dibs Bookings group only when the add-on is active.
  const { data: bookingsEnabled } = useQuery({
    queryKey: ["bookings-enabled", businessId],
    queryFn: async () => {
      if (!businessId) return false;
      const { data, error } = await supabase
        .from("businesses")
        .select("bookings_enabled")
        .eq("business_id", businessId)
        .maybeSingle();
      if (error) throw error;
      return data?.bookings_enabled !== false;
    },
    enabled: !!businessId && hasBookingsAddon,
  });
  const bookingsActive = hasBookingsAddon && bookingsEnabled === true;
  const filteredGroupsWithBookings = bookingsActive
    ? [
        ...filteredGroups,
        {
          key: "dibs-bookings" as const,
          label: "Dibs Bookings",
          collapsible: true,
          items: DIBS_BOOKINGS_ITEMS,
        },
      ]
    : filteredGroups;

  // Filter all items for pinned lookup
  const filteredAllItems = ALL_SIDEBAR_ITEMS.filter(itemAllowed);

  // Build pinned items in pin-time order
  const pinnedItems: SidebarItem[] = pinnedKeys
    .map((key) => filteredAllItems.find((i) => i.key === key))
    .filter((i): i is SidebarItem => !!i);

  // An item is active when its own section is open, or when the open section is
  // a sub-page that belongs to it (e.g. Customer details → Customers).
  // Items with sectionParams also require those extra URL params to match.
  const isItemActive = (item: SidebarItem) => {
    if (item.sectionParams) {
      return (
        activeSection === item.section &&
        Object.entries(item.sectionParams).every(([k, v]) => searchParams.get(k) === v)
      );
    }
    return activeSection === item.section || SECTION_PARENT_KEY[activeSection] === item.key;
  };

  return (
    <Sidebar data-tour="sidebar" collapsible="icon" className="border-r border-sidebar-border">
      <SidebarHeader className="gap-0 p-0">
        {/* Fixed h-12 band with a bottom border so the branch switcher lines up
            with the "Home" breadcrumb row and its divider in the main pane. */}
        <div data-tour="branch-selector" className="h-12 shrink-0 border-b border-sidebar-border">
          {isSettingsView ? (
            <button
              type="button"
              onClick={onExitSettings}
              className="flex h-full w-full items-center gap-2 px-3 text-left text-sm font-bold outline-none transition-colors hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:px-0"
              aria-label="Back from Settings"
            >
              <ArrowLeft className="h-4 w-4 shrink-0" />
              <span className="group-data-[collapsible=icon]:hidden">Settings</span>
            </button>
          ) : (
            <OutletSwitcher onSectionChange={onSectionChange} />
          )}
        </div>
        {!isSettingsView && isStaff && staffBusinesses && staffBusinesses.length > 1 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="mx-2 mt-2 flex w-[calc(100%-1rem)] items-center justify-between p-2 rounded-md hover:bg-muted transition-colors group-data-[collapsible=icon]:hidden">
                <div className="flex items-center gap-2.5">
                  <div className="w-7 h-7 rounded-md bg-primary/10 flex items-center justify-center">
                    <Building2 className="h-3.5 w-3.5 text-primary" />
                  </div>
                  <span className="font-medium text-sm truncate max-w-[120px]">
                    {staffBusinesses.find(b => b.business_id === businessId)?.business_name || "Loading..."}
                  </span>
                </div>
                <ChevronsUpDown className="h-4 w-4 text-muted-foreground" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-[var(--radix-dropdown-menu-trigger-width)]">
              {staffBusinesses.map((b) => (
                <DropdownMenuItem
                  key={b.business_id}
                  onClick={() => switchStaffBusiness(b.business_id)}
                  className={businessId === b.business_id ? "bg-muted font-medium" : ""}
                >
                  {b.business_name}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </SidebarHeader>

      <SidebarContent className="overscroll-none">
        {isSettingsView ? (
          <SidebarGroup>
            <div className="relative mb-3 group-data-[collapsible=icon]:hidden">
              <Search
                aria-hidden="true"
                className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
              />
              <input
                type="search"
                aria-label="Search settings"
                placeholder="Search settings..."
                value={settingsSearch}
                onChange={(event) => setSettingsSearch(event.target.value)}
                className="h-9 w-full rounded-md border border-sidebar-border bg-background pl-8 pr-3 text-sm text-foreground outline-none placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-sidebar-ring"
              />
            </div>
            <SidebarGroupContent className="space-y-2">
              {SETTINGS_NAV_GROUPS.map((group) => {
                const groupItems = visibleSettingsItems.filter((item) => item.group === group.key);
                if (groupItems.length === 0) return null;

                return (
                  <div key={group.key}>
                    <SidebarGroupLabel>{group.label}</SidebarGroupLabel>
                    <SidebarMenu className="gap-2">
                      {groupItems.map((item) => {
                        const ItemIcon = SETTINGS_ITEM_ICONS[item.key];
                        return (
                          <SidebarMenuItem key={item.key}>
                            <SidebarMenuButton
                              isActive={activeSettingsPanel === item.key}
                              tooltip={item.label}
                              data-tour={`settings-${item.key}`}
                              onClick={() => onSectionChange(
                                "settings",
                                item.key === "account" ? undefined : { settings: item.key },
                              )}
                              aria-current={activeSettingsPanel === item.key ? "page" : undefined}
                            >
                              <ItemIcon className="h-4 w-4 shrink-0" />
                              <span>{item.label}</span>
                            </SidebarMenuButton>
                          </SidebarMenuItem>
                        );
                      })}
                    </SidebarMenu>
                  </div>
                );
              })}
              {visibleSettingsItems.length === 0 && (
                <p className="px-2 py-3 text-sm text-muted-foreground" role="status">
                  No settings found
                </p>
              )}
            </SidebarGroupContent>
          </SidebarGroup>
        ) : (
          <>
        {/* ── Pinned Tabs (top of sidebar; hidden entirely when none) ── */}
        {SHOW_PINNED_TABS && !isStaff && pinnedItems.length > 0 && (
          <>
            <SidebarGroup>
              <SidebarGroupLabel>Pinned tabs</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu className="gap-2">
                  {pinnedItems.map((item) => (
                    <SidebarTabItem
                      key={`pinned-${item.key}`}
                      item={item}
                      isActive={activeSection === item.section}
                      isPinned={true}
                      hidePinAction={true}
                      onToggleActive={() => onSectionChange(item.section)}
                      onTogglePin={() => togglePin(item.key)}
                      onPreview={item.hasPreviewIcon ? openStorefrontPreview : undefined}
                      isLocked={itemLocked(item)}
                      {...badgeProps(item)}
                    />
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
            <SidebarSeparator className="mx-0" />
          </>
        )}

        {/* ── Regular groups ── */}
        {filteredGroupsWithBookings.map((group) => {
          const groupIsCollapsed = collapsedKeys.has(group.key);

          return (
            <SidebarGroup key={group.key}>
              {group.key === "dibs-bookings" && (
                <SidebarSeparator
                  data-testid="connected-compact-separator"
                  className="mx-0 mb-2 mt-1 hidden group-data-[collapsible=icon]:block"
                />
              )}
              {group.label && (
                group.collapsible ? (
                  <SidebarGroupLabel asChild>
                    <button
                      type="button"
                      aria-expanded={!groupIsCollapsed}
                      aria-controls={`sidebar-group-${group.key}`}
                      onClick={() => toggleCollapsed(group.key)}
                      className="w-full cursor-pointer hover:bg-sidebar-accent hover:text-sidebar-accent-foreground group-data-[collapsible=icon]:hidden"
                    >
                      <span className="flex-1 text-left">{group.label}</span>
                      {groupIsCollapsed
                        ? <ChevronRight aria-hidden="true" />
                        : <ChevronDown aria-hidden="true" />}
                    </button>
                  </SidebarGroupLabel>
                ) : (
                  <SidebarGroupLabel>{group.label}</SidebarGroupLabel>
                )
              )}
              <SidebarGroupContent
                id={`sidebar-group-${group.key}`}
                hidden={group.collapsible && groupIsCollapsed}
              >
                <SidebarMenu className="gap-2">
                  {group.items.map((item) =>
                    item.children && item.children.length > 0 ? (
                      // Parent item — clickable if it has a section, otherwise a non-clickable header.
                      <SidebarMenuItem key={item.key} data-tour={`sidebar-${item.key}`} className="pb-1">
                        <SidebarMenuButton
                          isActive={isItemActive(item)}
                          onClick={item.section ? () => onSectionChange(item.section) : undefined}
                          className={item.section ? undefined : "cursor-default hover:bg-transparent"}
                        >
                          <item.icon className="h-4 w-4 shrink-0" />
                          <span className="flex-1 truncate">{item.label}</span>
                          <span
                            role="button"
                            tabIndex={-1}
                            onClick={(e) => { e.stopPropagation(); toggleCollapsed(item.key); }}
                            className="ml-auto inline-flex items-center justify-center h-5 w-5 rounded-sm hover:bg-muted-foreground/10 cursor-pointer"
                          >
                            {collapsedKeys.has(item.key)
                              ? <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                              : <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />}
                          </span>
                        </SidebarMenuButton>
                        {!collapsedKeys.has(item.key) && (
                          <SidebarMenuSub className="gap-2 mr-0 pr-0 mt-2">
                            {item.children.map((child) => (
                              <SidebarTabItem
                                key={child.key}
                                item={child}
                                isActive={isItemActive(child)}
                                isPinned={pinnedKeys.includes(child.key)}
                                onToggleActive={() => onSectionChange(child.section, child.sectionParams)}
                                onTogglePin={() => togglePin(child.key)}
                                onPreview={child.hasPreviewIcon ? openStorefrontPreview : undefined}
                                hidePinAction={isStaff || !SHOW_PINNED_TABS}
                                dataTour={`sidebar-${child.key}`}
                                isLocked={itemLocked(child)}
                              />
                            ))}
                          </SidebarMenuSub>
                        )}
                      </SidebarMenuItem>
                    ) : (
                      <SidebarTabItem
                        key={item.key}
                        item={item}
                        isActive={isItemActive(item)}
                        isPinned={pinnedKeys.includes(item.key)}
                        onToggleActive={() => onSectionChange(item.section)}
                        onTogglePin={() => togglePin(item.key)}
                        onPreview={item.hasPreviewIcon ? openStorefrontPreview : undefined}
                        hidePinAction={isStaff || !SHOW_PINNED_TABS}
                        dataTour={`sidebar-${item.key}`}
                        isLocked={itemLocked(item)}
                        {...badgeProps(item)}
                      />
                    ),
                  )}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          );
        })}
          </>
        )}
      </SidebarContent>

      <SidebarFooter className="p-2 space-y-2">
        {/* Staff keep a lightweight account menu (sign out only) here since the
           branch selector doesn't expose account actions for them. Owners manage
           their account entirely from the branch selector at the top. */}
        {isStaff && (
          <SidebarMenu>
            <SidebarMenuItem>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="w-full flex items-center gap-2 rounded-lg p-2 text-left hover:bg-muted transition-colors group group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:p-0">
                    <Avatar className="h-8 w-8 rounded-lg">
                      <AvatarFallback className="rounded-lg bg-primary/10 text-primary font-semibold text-xs">{userInitial}</AvatarFallback>
                    </Avatar>
                    <div className="flex flex-col min-w-0 flex-1 leading-tight group-data-[collapsible=icon]:hidden">
                      <span className="text-xs font-medium truncate text-foreground">{user?.email}</span>
                      <span className="text-[11px] text-muted-foreground capitalize truncate">Staff Account</span>
                    </div>
                    <MoreVertical className="ml-auto h-4 w-4 shrink-0 text-muted-foreground group-data-[collapsible=icon]:hidden" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" side="top" className="w-[var(--radix-dropdown-menu-trigger-width)]">
                  <DropdownMenuItem onClick={handleSignOut}>
                    <LogOut className="h-4 w-4 mr-2" />
                    Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </SidebarMenuItem>
          </SidebarMenu>
        )}

        {!isStaff && trialDaysRemaining !== null && (
          <div className="rounded-xl bg-white p-3 shadow-sm group-data-[collapsible=icon]:hidden">
            <p className="flex items-center justify-center gap-1.5 text-center text-sm font-semibold text-foreground">
              {trialDaysRemaining} {trialDaysRemaining === 1 ? "day" : "days"} left on trial
              <Rocket className="animate-trial-rocket h-4 w-4 text-primary" aria-hidden="true" />
            </p>
            <button
              type="button"
              onClick={() => onSectionChange("settings", { settings: "billing" })}
              className="mt-2.5 w-full rounded-lg bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring focus-visible:ring-offset-2"
            >
              Keep Premium
            </button>
          </div>
        )}

        <SidebarSeparator className="-mx-2" />

        <div className="flex items-center gap-2 px-2 pt-0.5 pb-3 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:px-0">
          <img
            alt="Dibs"
            className="h-6 w-6 object-contain"
            src="/dibs_assets/red_logo_with_transparent_bg.png"
          />
          <span className="font-heading text-base font-semibold text-primary group-data-[collapsible=icon]:hidden">Dibs</span>
        </div>
      </SidebarFooter>
    </Sidebar>
  );
};

export default AdminSidebar;
